import { defineStore } from 'pinia'

import type {
  CountryRecord,
  LocationCategoryRecord,
  LocationRecord,
  SiteLocale
} from '~/types/datocms'

export interface LocationStoreState {
  allLocations: LocationRecord[]
  allLocationsCount: number
  allCategories: LocationCategoryRecord[]
  allCountries: CountryRecord[]
  isLoading: boolean
  isLoadingMore: boolean
  itemsPerPage: number
  isButtonVisible: boolean
  skipItems: number
}

export const useLocationStore = defineStore('LocationStore', {
  state: (): LocationStoreState => ({
    allLocations: [],
    allLocationsCount: 0,
    allCategories: [],
    allCountries: [],
    isLoading: true,
    isLoadingMore: false,
    itemsPerPage: 10,
    isButtonVisible: false,
    skipItems: 0
  }),
  getters: {
    getCategoryById(state) {
      return (id: string) => state.allCategories.find((cat) => cat.id === id) || null
    },
    getCountryById(state) {
      return (id: string) => state.allCountries.find((cat) => cat.id === id) || null
    },
    getCategoryBySlug(state) {
      return (catSlug: string) => state.allCategories.find((cat) => cat.slug === catSlug) || null
    },
    getCountryBySlug(state) {
      return (catSlug: string) => state.allCountries.find((cat) => cat.slug === catSlug) || null
    }
  },
  actions: {
    async fetchLocations(locale: string, category?: string, country?: string) {
      this.setButtonVisibility(false)
      try {
        const { data } = await useLocations({
          variables: {
            locale: locale as unknown as SiteLocale,
            first: this.itemsPerPage,
            skip: this.skipItems,
            allInCategory: category,
            allInCountry: country
          }
        })

        this.allLocationsCount = data?._allLocationsMeta?.count

        const allLocationsRes: LocationRecord[] = data.allLocations as LocationRecord[]

        if (allLocationsRes.length > 0) {
          this.allLocations.push(...allLocationsRes)
          this.skipItems += allLocationsRes.length
        }
      } catch (error) {
        console.error(error)
      } finally {
        useTimeoutFn(() => {
          this.setLoading(false)
          this.setLoadingMore(false)
        }, 600)

        useTimeoutFn(() => {
          this.setButtonVisibility()
        }, 1000)
      }
    },
    setItemsPerPage(amount: number) {
      this.itemsPerPage = amount
    },
    setLoading(bool: boolean) {
      this.isLoading = bool
    },
    setLoadingMore(bool: boolean) {
      this.isLoadingMore = bool
    },
    setButtonVisibility(bool?: boolean) {
      this.isButtonVisible = bool || this.skipItems < this.allLocationsCount
    },
    pushCategories(payload: LocationStoreState['allCategories']) {
      this.allCategories = payload
    },
    pushCountries(payload: LocationStoreState['allCountries']) {
      this.allCountries = payload
    },
    resetLocations() {
      this.allLocations = []
      this.allLocationsCount = 0
      this.skipItems = 0
    }
  }
})
